import { useEffect, useRef } from "react";
import { env } from "@/shared/constants/env";
import { useSessionStore } from "@/shared/stores/sessionStore";
import {
  isWebPushSupported,
  listenForForegroundMessages,
  refreshDeviceRegistration,
  registerWebPush,
} from "./webPush";

/** Rafraîchit FCM au démarrage, à la reconnexion et au retour dans l’onglet.
 * Tente la permission après connexion, avec un repli sur un geste utilisateur.
 */
export function useDeviceRegistration(): void {
  const userId = useSessionStore((s) => s.userId);
  // Survit au double effet de StrictMode, sans verrou persistant qui pourrait
  // empêcher une nouvelle demande alors que le navigateur n’a rien affiché.
  const attemptRef = useRef<{
    userId: string;
    started: boolean;
    inFlight: boolean;
    retried: boolean;
  } | null>(null);

  useEffect(() => {
    if (!userId) {
      attemptRef.current = null;
      return;
    }
    if (attemptRef.current?.userId !== userId) {
      attemptRef.current = { userId, started: false, inFlight: false, retried: false };
    }
    const attempt = attemptRef.current;

    const refresh = () => void refreshDeviceRegistration(userId);

    let disposed = false;
    let stopListening: (() => void) | undefined;
    void listenForForegroundMessages().then((stop) => {
      if (disposed) stop(); else stopListening = stop;
    }).catch(() => undefined);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const removePermissionListeners = () => {
      window.removeEventListener("click", requestPermission, true);
      window.removeEventListener("keydown", requestPermission, true);
    };
    const activate = () => {
      attempt.inFlight = true;
      // Appel synchrone : le repli conserve l’activation du geste utilisateur.
      void registerWebPush(userId).catch((error) => {
        if (env.isDev) console.warn("[push] demande de permission ou enregistrement échoué", error);
      }).finally(() => {
        attempt.inFlight = false;
      });
    };
    const requestPermission = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key !== "Enter" && event.key !== " ") return;
      if (navigator.userActivation && !navigator.userActivation.isActive) return;
      if (useSessionStore.getState().userId !== userId) return;
      if (!isWebPushSupported()) return;
      if (Notification.permission !== "default" || attempt.retried) {
        removePermissionListeners();
        return;
      }
      if (attempt.inFlight) return;
      // Une seule reprise sur interaction, pas une invite à chaque clic.
      attempt.retried = true;
      removePermissionListeners();
      activate();
    };
    if (isWebPushSupported() && Notification.permission === "default") {
      window.addEventListener("click", requestPermission, true);
      window.addEventListener("keydown", requestPermission, true);
      if (!attempt.started) {
        attempt.started = true;
        activate();
      }
    }
    refresh();
    // « Après chaque reconnexion » : un abonnement peut avoir été renouvelé par
    // le navigateur pendant la coupure.
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      disposed = true;
      stopListening?.();
      removePermissionListeners();
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userId]);
}
